import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance, { API_BASE_URL } from '../utils/axiosConfig';
import {
    DaireAkilliKriter, AkilliFiltreCipi, metindenKriter, etkinAnahtarKelimeler, etkinDaireNolari,
    blokKatSiralari, akilliFiltreUyar, akilliFiltreCipleri,
} from '../utils/daireAkilliFiltre';
import DaireDetayModal from '../components/DaireDetayModal';
import ProjeGorunumleri from '../components/ProjeGorunumleri';

interface GaleriOgesi { projeGaleriID: number; gorselYolu: string; aciklama?: string | null; }
interface MaddeOgesi { projeMaddeID: number; ikon?: string | null; metin: string; }
interface ProjeOzellik { projeOzellikId: number; ozellik: string; deger: string; ikon?: string | null; }
interface BolumGorsel { projeDaireTipiBolumGorselID: number; gorselYolu: string; }
interface DaireTipiBolum { projeDaireTipiBolumID: number; gorseller: BolumGorsel[]; bolumAdi: string; aciklama?: string | null; ikon?: string | null; }
interface DaireTipiSatir { daireTipID: number; tip: string; ikon: string | null; bolumler: DaireTipiBolum[]; }
interface Video { projeVideoID: number; youtubeLink: string; baslik?: string | null; }
interface Bolum { gorseller: GaleriOgesi[]; maddeler: MaddeOgesi[]; }

interface DaireItem {
    daireID: number;
    daireNo: string;
    daireDurumu: string;
    blokID: number;
    katID?: number;
    cepheID?: number;
    manzaraID?: number;
    daireTipID?: number;
    aktifMi?: boolean;
    takipNo?: string | null;
    aciklama?: string | null;
    listeFiyati?: number | null;
    netM2?: number | null;
    brutM2?: number | null;
    proje?: { projeID?: number; projeAdi?: string; projeDurumu?: string; sehir?: string | null; takipNo?: string | null } | null;
    blok?: { blokAdi?: string } | null;
    kat?: { katAdi?: string; sira?: number | null } | null;
    cephe?: { cepheAdi?: string } | null;
    manzara?: { manzaraAdi?: string } | null;
    daireTipi?: { tip?: string } | null;
}

interface GorunumOzet {
    gorunumID: number;
    gorunumAdi: string;
    gorunumTipi?: string;
    arkaplanGorsel?: string | null;
}

interface LookupItem { id: number; ad: string; }

interface ProjeSayfaVeri {
    projeID: number;
    projeAdi: string;
    projeLogo: string | null;
    hakkindaMetni: string | null;
    kapakGorselleri: GaleriOgesi[];
    vaziyetPlaniGorselleri: GaleriOgesi[];
    videolar: Video[];
    ozellikler: ProjeOzellik[];
    daireTipleri: DaireTipiSatir[];
    disMekan: Bolum;
    konumCevre: Bolum;
    icMekan: Bolum;
    sosyalAlan: Bolum;
}

const youtubeEmbed = (link: string): string | null => {
    const m = link.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{6,})/);
    return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

// JS'in varsayılan /i bayrağı Türkçe büyük İ'yi ASCII 'i' ile eşleştirmiyor, bu yüzden Türkçe locale kullanılıyor.
const katSirasiHesapla = (katAdi: string): number => {
    const normalized = katAdi.toLocaleLowerCase('tr-TR');
    if (normalized.includes('bodrum')) return -1;
    if (normalized.includes('zemin')) return 0;
    const m = katAdi.match(/\d+/);
    return m ? parseInt(m[0]) : 99;
};

const katKarsilastir = (a: { katAdi?: string; sira?: number | null }, b: { katAdi?: string; sira?: number | null }): number => {
    const aSira = a.sira ?? katSirasiHesapla(a.katAdi || '');
    const bSira = b.sira ?? katSirasiHesapla(b.katAdi || '');
    if (aSira !== bSira) return aSira - bSira;
    return katSirasiHesapla(a.katAdi || '') - katSirasiHesapla(b.katAdi || '');
};

const daireSirala = (a: DaireItem, b: DaireItem): number => {
    const blokCmp = (a.blok?.blokAdi || '').localeCompare(b.blok?.blokAdi || '', 'tr');
    if (blokCmp !== 0) return blokCmp;
    const katCmp = katKarsilastir(a.kat || {}, b.kat || {});
    if (katCmp !== 0) return katCmp;
    return (a.daireNo || '').localeCompare(b.daireNo || '', 'tr', { numeric: true });
};

const fmtFiyat = (v?: number | null): string => (v ? `${v.toLocaleString('tr-TR')} TL` : '-');

const DURUM_RENK: Record<string, { bg: string; color: string }> = {
    'Satışta': { bg: '#dcfce7', color: '#15803d' },
    'Rezerve': { bg: '#fef9c3', color: '#ca8a04' },
    'Sözleşme': { bg: '#fee2e2', color: '#b91c1c' },
};
const durumStil = (durum: string) => DURUM_RENK[durum] || { bg: '#f1f5f9', color: '#475569' };

const formatFiyatStr = (val: string): string => {
    const raw = val.replace(/\D/g, '');
    return raw ? Number(raw).toLocaleString('tr-TR') : '';
};
const parseFiyatStr = (val: string): number => Number(val.replace(/\./g, '').replace(/,/g, ''));

const DURUM_PILLS_PS: { value: string; label: string; dot: string }[] = [
    { value: '', label: 'Tümü', dot: '#94a3b8' },
    { value: 'Satışta', label: 'Satışta', dot: '#22c55e' },
    { value: 'Rezerve', label: 'Rezerve', dot: '#f59e0b' },
    { value: 'Sözleşme', label: 'Sözleşme', dot: '#ef4444' },
];

const NAV: { key: string; ad: string }[] = [
    { key: 'hakkinda', ad: 'Hakkında' },
    { key: 'ozellikler', ad: 'Özellikler' },
    { key: 'vaziyet', ad: 'Vaziyet Planı' },
    { key: 'galeri', ad: 'Galeri' },
    { key: 'bloklar', ad: 'Bloklar' },
    { key: 'gorunumler', ad: 'Görünümler' },
    { key: 'daireler', ad: 'Daireler' },
];

const GALERI_TABS: { key: string; ad: string }[] = [
    { key: 'daireTipleri', ad: 'Daire Tipleri' },
    { key: 'video', ad: 'Video' },
    { key: 'disMekan', ad: 'Dış Mekan' },
    { key: 'konumCevre', ad: 'Konum / Çevre' },
    { key: 'icMekan', ad: 'İç Mekan' },
    { key: 'sosyalAlan', ad: 'Sosyal Alanlar' },
];

interface GaleriResim { gorselYolu: string; aciklama?: string | null; kisim: string; }

const GorselKart: React.FC<{ oge: GaleriOgesi; onClick: () => void }> = ({ oge, onClick }) => (
    <div onClick={onClick} style={{ position: 'relative', aspectRatio: '4/3', borderRadius: 12, overflow: 'hidden', cursor: 'pointer', background: '#f1f5f9' }}>
        <img src={`${API_BASE_URL}/userFiles/${oge.gorselYolu}`} alt={oge.aciklama || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        {oge.aciklama && (
            <div style={{ position: 'absolute', inset: 'auto 0 0 0', background: 'linear-gradient(transparent, rgba(0,0,0,0.65))', color: '#fff', fontSize: 12, fontWeight: 600, padding: '20px 12px 8px' }}>
                {oge.aciklama}
            </div>
        )}
    </div>
);

const MaddeListesi: React.FC<{ maddeler: MaddeOgesi[] }> = ({ maddeler }) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
        {maddeler.map(m => (
            <div key={m.projeMaddeID} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#f8fafc', borderRadius: 10 }}>
                <i className={m.ikon || 'fas fa-check-circle'} style={{ color: '#cd6906', fontSize: 15 }}></i>
                <span style={{ fontSize: 13.5, color: '#334155', fontWeight: 500 }}>{m.metin}</span>
            </div>
        ))}
    </div>
);

const BolumIcerik: React.FC<{ bolum: Bolum; onGorsel: (o: GaleriOgesi) => void }> = ({ bolum, onGorsel }) => {
    if (bolum.gorseller.length === 0 && bolum.maddeler.length === 0) {
        return <div style={{ color: '#94a3b8', fontSize: 13, padding: '20px 0' }}>Bu bölüm için henüz içerik eklenmedi.</div>;
    }
    return (
        <div>
            {bolum.gorseller.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginBottom: bolum.maddeler.length > 0 ? 20 : 0 }}>
                    {bolum.gorseller.map(g => <GorselKart key={g.projeGaleriID} oge={g} onClick={() => onGorsel(g)} />)}
                </div>
            )}
            {bolum.maddeler.length > 0 && <MaddeListesi maddeler={bolum.maddeler} />}
        </div>
    );
};

// Daire tipi detayı: bölümler üstte sekme kartları, seçili bölümün görselleri kaydırmalı (ok, klavye, dokunma).
const DaireTipiModal: React.FC<{ tip: DaireTipiSatir; daireSayisi: number; onClose: () => void; onZoom: (gorselYolu: string) => void }> = ({ tip, daireSayisi, onClose, onZoom }) => {
    const [bolumIdx, setBolumIdx] = useState(0);
    const [gorselIdx, setGorselIdx] = useState(0);
    const [dokunmaX, setDokunmaX] = useState<number | null>(null);
    const bolum = tip.bolumler[bolumIdx];
    const gorseller = bolum?.gorseller ?? [];

    const bolumSec = (i: number) => { setBolumIdx(i); setGorselIdx(0); };
    const ileri = () => setGorselIdx(i => gorseller.length ? (i + 1) % gorseller.length : 0);
    const geri = () => setGorselIdx(i => gorseller.length ? (i - 1 + gorseller.length) % gorseller.length : 0);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight') ileri();
            if (e.key === 'ArrowLeft') geri();
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gorseller.length, onClose]);

    const okBtn = (yon: 'sol' | 'sag'): React.CSSProperties => ({
        position: 'absolute', top: '50%', [yon === 'sol' ? 'left' : 'right']: 14, transform: 'translateY(-50%)',
        width: 44, height: 44, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.9)', color: '#0f172a',
        cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.2)', fontSize: 15,
    });

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 1240, height: 'min(92vh, 900px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ padding: '16px 22px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, fontSize: 17, color: '#0f172a' }}>
                        <i className={tip.ikon || 'fas fa-door-open'} style={{ color: '#cd6906' }}></i>{tip.tip}
                        <span style={{ fontSize: 12.5, fontWeight: 500, color: '#94a3b8' }}>· {daireSayisi} daire</span>
                    </div>
                    <button onClick={onClose} title="Kapat (Esc)" style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: '#f1f5f9', color: '#64748b', cursor: 'pointer' }}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {/* Bölüm sekmeleri */}
                <div style={{ display: 'flex', gap: 12, padding: '16px 22px 18px', overflowX: 'auto', flexShrink: 0 }}>
                    {tip.bolumler.map((b, i) => {
                        const aktif = i === bolumIdx;
                        return (
                            <button key={b.projeDaireTipiBolumID} onClick={() => bolumSec(i)}
                                style={{
                                    position: 'relative', flex: '1 0 130px', maxWidth: 220, minHeight: 104, padding: '16px 10px 14px',
                                    border: 'none', borderRadius: 4, cursor: 'pointer',
                                    background: aktif ? '#cd6906' : '#eef3f6', color: aktif ? '#fff' : '#1e293b',
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12,
                                    transition: 'background 0.15s',
                                }}>
                                <i className={b.ikon || 'fas fa-layer-group'} style={{ fontSize: 30, color: aktif ? '#fff' : '#cd6906' }}></i>
                                <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>{b.bolumAdi}</span>
                                {aktif && (
                                    <span style={{ position: 'absolute', bottom: -9, left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '10px solid transparent', borderRight: '10px solid transparent', borderTop: '10px solid #cd6906' }} />
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Seçili bölüm */}
                {bolum && (
                    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '0 22px 20px', gap: 12 }}>
                        <div
                            onTouchStart={e => setDokunmaX(e.touches[0].clientX)}
                            onTouchEnd={e => {
                                if (dokunmaX === null) return;
                                const fark = e.changedTouches[0].clientX - dokunmaX;
                                if (Math.abs(fark) > 40) { if (fark < 0) ileri(); else geri(); }
                                setDokunmaX(null);
                            }}
                            style={{ position: 'relative', flex: 1, minHeight: 0, background: '#0f172a', borderRadius: 12, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {gorseller.length > 0 ? (
                                <img key={gorseller[gorselIdx]?.gorselYolu} src={`${API_BASE_URL}/userFiles/${gorseller[gorselIdx]?.gorselYolu}`} alt={bolum.bolumAdi}
                                    onClick={() => onZoom(gorseller[gorselIdx].gorselYolu)}
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', cursor: 'zoom-in' }} />
                            ) : (
                                <i className={bolum.ikon || 'fas fa-image'} style={{ fontSize: 48, color: 'rgba(255,255,255,0.2)' }}></i>
                            )}
                            {gorseller.length > 1 && (
                                <>
                                    <button onClick={geri} title="Önceki" style={okBtn('sol')}><i className="fas fa-chevron-left"></i></button>
                                    <button onClick={ileri} title="Sonraki" style={okBtn('sag')}><i className="fas fa-chevron-right"></i></button>
                                    <span style={{ position: 'absolute', bottom: 12, right: 14, background: 'rgba(15,23,42,0.7)', color: '#fff', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 9999 }}>
                                        {gorselIdx + 1} / {gorseller.length}
                                    </span>
                                </>
                            )}
                        </div>

                        {gorseller.length > 1 && (
                            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', flexShrink: 0 }}>
                                {gorseller.map((g, i) => (
                                    <img key={g.projeDaireTipiBolumGorselID} src={`${API_BASE_URL}/userFiles/${g.gorselYolu}`} alt="" onClick={() => setGorselIdx(i)}
                                        style={{ width: 84, height: 60, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', flexShrink: 0, opacity: i === gorselIdx ? 1 : 0.55, outline: i === gorselIdx ? '2px solid #cd6906' : 'none', outlineOffset: 1 }} />
                                ))}
                            </div>
                        )}

                        {bolum.aciklama && (
                            <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.7, whiteSpace: 'pre-wrap', flexShrink: 0, maxHeight: 120, overflowY: 'auto' }}>{bolum.aciklama}</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const ProjeSayfaGoruntule: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [veri, setVeri] = useState<ProjeSayfaVeri | null>(null);
    const [yukleniyor, setYukleniyor] = useState(true);
    const [kapakIndex, setKapakIndex] = useState(0);
    const [lightbox, setLightbox] = useState<string | null>(null);
    const [galeriLightboxIndex, setGaleriLightboxIndex] = useState<number | null>(null);
    const [galeriTab, setGaleriTab] = useState('daireTipleri');
    const [detayTip, setDetayTip] = useState<DaireTipiSatir | null>(null);
    const [aktifNav, setAktifNav] = useState('hakkinda');
    const [dairelerAcik, setDairelerAcik] = useState(false);

    const [daireler, setDaireler] = useState<DaireItem[]>([]);
    const [daireYukleniyor, setDaireYukleniyor] = useState(true);
    const [akilliMetin, setAkilliMetin] = useState('');
    const [akilliKriter, setAkilliKriter] = useState<DaireAkilliKriter | null>(null);
    const [blokFiltre, setBlokFiltre] = useState('');
    const [tipFiltre, setTipFiltre] = useState('');
    const [durumFiltre, setDurumFiltre] = useState<string[]>(['Satışta']);
    const [minFiyat, setMinFiyat] = useState('');
    const [maxFiyat, setMaxFiyat] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [colVisible, setColVisible] = useState({ cephe: true, manzara: true, m2: true, fiyat: true });
    const [daireSayfa, setDaireSayfa] = useState(1);
    const [detayDaire, setDetayDaire] = useState<DaireItem | null>(null);

    const [gorunumlerListesi, setGorunumlerListesi] = useState<GorunumOzet[]>([]);
    const [gorunumlerYukleniyor, setGorunumlerYukleniyor] = useState(true);
    const [acikGorunumID, setAcikGorunumID] = useState<number | null>(null);
    const [gorunumLookupYukleniyor, setGorunumLookupYukleniyor] = useState(false);
    const [gorunumBloklar, setGorunumBloklar] = useState<LookupItem[]>([]);
    const [gorunumKatlar, setGorunumKatlar] = useState<LookupItem[]>([]);
    const [gorunumCepheler, setGorunumCepheler] = useState<LookupItem[]>([]);
    const [gorunumManzaralar, setGorunumManzaralar] = useState<LookupItem[]>([]);
    const [gorunumDaireTipleri, setGorunumDaireTipleri] = useState<LookupItem[]>([]);

    useEffect(() => {
        if (!id) return;
        // Bu özellik (Proje Sayfası) henüz her ortamda yayında değil — API bu uç
        // noktayı tanımıyorsa (404) veya ilgili tablolar veritabanında yoksa (500),
        // kullanıcıyı kırık bir sayfada bırakmak yerine sessizce mevcut Proje
        // Detayı (yönetim) sayfasına yönlendiriyoruz.
        axiosInstance.get(`/api/ProjeSayfa/${id}`)
            .then(r => { setVeri(r.data); setYukleniyor(false); })
            .catch(() => navigate(`/projeler/${id}`, { replace: true }));
    }, [id, navigate]);

    useEffect(() => {
        if (!id) return;
        setDaireYukleniyor(true);
        axiosInstance.get('/api/Daireler', { params: { projeID: id, aktifMi: true } })
            .then(r => setDaireler(r.data))
            .catch(() => setDaireler([]))
            .finally(() => setDaireYukleniyor(false));
    }, [id]);

    useEffect(() => {
        if (!id) return;
        setGorunumlerYukleniyor(true);
        axiosInstance.get('/api/ProjeGorunumleri', { params: { projeID: id } })
            .then(r => setGorunumlerListesi(r.data || []))
            .catch(() => setGorunumlerListesi([]))
            .finally(() => setGorunumlerYukleniyor(false));
    }, [id]);

    const acGorunum = async (gorunumID: number) => {
        if (gorunumBloklar.length === 0 && gorunumKatlar.length === 0) {
            setGorunumLookupYukleniyor(true);
            try {
                const [bRes, kRes, cRes, mRes, dtRes] = await Promise.all([
                    axiosInstance.get(`/api/Bloklar/proje/${id}`),
                    axiosInstance.get('/api/Katlar'),
                    axiosInstance.get('/api/Cepheler'),
                    axiosInstance.get('/api/Manzaralar'),
                    axiosInstance.get('/api/DaireTipleri'),
                ]);
                setGorunumBloklar((bRes.data || []).map((b: any) => ({ id: b.blokID, ad: b.blokAdi })));
                setGorunumKatlar((kRes.data || []).map((k: any) => ({ id: k.katID, ad: k.katAdi })));
                setGorunumCepheler((cRes.data || []).map((c: any) => ({ id: c.cepheID, ad: c.cepheAdi })));
                setGorunumManzaralar((mRes.data || []).map((m: any) => ({ id: m.manzaraID, ad: m.manzaraAdi })));
                setGorunumDaireTipleri((dtRes.data || []).map((dt: any) => ({ id: dt.daireTipID, ad: dt.tip })));
            } catch { /* sessiz */ }
            finally { setGorunumLookupYukleniyor(false); }
        }
        setAcikGorunumID(gorunumID);
    };

    useEffect(() => {
        const metin = akilliMetin.trim();
        if (metin.length < 2) { setAkilliKriter(null); return; }
        const zamanlayici = setTimeout(() => {
            setAkilliKriter(metindenKriter(metin, veri ? [veri.projeAdi] : []));
        }, 250);
        return () => clearTimeout(zamanlayici);
    }, [akilliMetin, veri]);

    const bloklarListesi = useMemo(() => {
        const map = new Map<number, string>();
        daireler.forEach(d => { if (d.blok?.blokAdi) map.set(d.blokID, d.blok.blokAdi); });
        return Array.from(map.entries()).map(([blokID, blokAdi]) => ({ blokID, blokAdi })).sort((a, b) => a.blokAdi.localeCompare(b.blokAdi, 'tr'));
    }, [daireler]);

    const bloklarOzet = useMemo(() => {
        const map = new Map<number, { blokAdi: string; katSet: Set<string>; daireSayisi: number; tipSet: Set<string> }>();
        daireler.forEach(d => {
            if (!map.has(d.blokID)) map.set(d.blokID, { blokAdi: d.blok?.blokAdi || '—', katSet: new Set(), daireSayisi: 0, tipSet: new Set() });
            const b = map.get(d.blokID)!;
            b.daireSayisi += 1;
            if (d.kat?.katAdi) b.katSet.add(d.kat.katAdi);
            if (d.daireTipi?.tip) b.tipSet.add(d.daireTipi.tip);
        });
        return Array.from(map.entries())
            .map(([blokID, v]) => ({ blokID, blokAdi: v.blokAdi, katSayisi: v.katSet.size, daireSayisi: v.daireSayisi, daireTipiSayisi: v.tipSet.size }))
            .sort((a, b) => a.blokAdi.localeCompare(b.blokAdi, 'tr'));
    }, [daireler]);

    const daireTipiSayilari = useMemo(() => {
        const map = new Map<number, number>();
        daireler.forEach(d => {
            if (d.daireTipID != null) map.set(d.daireTipID, (map.get(d.daireTipID) || 0) + 1);
        });
        return map;
    }, [daireler]);

    const tipListesi = useMemo(() => {
        const set = new Set<string>();
        daireler.forEach(d => { if (d.daireTipi?.tip) set.add(d.daireTipi.tip); });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
    }, [daireler]);

    const statCounts = useMemo(() => ({
        toplam: daireler.length,
        satista: daireler.filter(d => d.daireDurumu === 'Satışta').length,
        rezerve: daireler.filter(d => d.daireDurumu === 'Rezerve').length,
        sozlesme: daireler.filter(d => d.daireDurumu === 'Sözleşme').length,
    }), [daireler]);

    const { filtrelenmisDaireler, akilliCipler } = useMemo(() => {
        let sonuc = [...daireler];
        let cipler: AkilliFiltreCipi[] = [];
        if (akilliKriter) {
            const kelimeler = etkinAnahtarKelimeler(akilliKriter, daireler);
            const daireNolar = etkinDaireNolari(akilliKriter, daireler);
            cipler = akilliFiltreCipleri(akilliKriter, kelimeler, daireNolar);
            if (cipler.length > 0) {
                const siralar = blokKatSiralari(daireler, katSirasiHesapla);
                sonuc = sonuc.filter(d => akilliFiltreUyar(d, akilliKriter, kelimeler, daireNolar, siralar, katSirasiHesapla));
            }
        }
        if (blokFiltre) sonuc = sonuc.filter(d => String(d.blokID) === blokFiltre);
        if (tipFiltre) sonuc = sonuc.filter(d => d.daireTipi?.tip === tipFiltre);
        if (durumFiltre.length > 0) sonuc = sonuc.filter(d => durumFiltre.includes(d.daireDurumu));
        const min = minFiyat ? parseFiyatStr(minFiyat) : null;
        const max = maxFiyat ? parseFiyatStr(maxFiyat) : null;
        if (min !== null) sonuc = sonuc.filter(d => (d.listeFiyati ?? 0) >= min);
        if (max !== null) sonuc = sonuc.filter(d => (d.listeFiyati ?? 0) <= max);

        if (sortBy === 'fiyat-artan') sonuc.sort((a, b) => (a.listeFiyati ?? 0) - (b.listeFiyati ?? 0));
        else if (sortBy === 'fiyat-azalan') sonuc.sort((a, b) => (b.listeFiyati ?? 0) - (a.listeFiyati ?? 0));
        else if (sortBy === 'daire-no') sonuc.sort((a, b) => (a.daireNo || '').localeCompare(b.daireNo || '', 'tr', { numeric: true }));
        else sonuc.sort(daireSirala);

        return { filtrelenmisDaireler: sonuc, akilliCipler: cipler };
    }, [daireler, akilliKriter, blokFiltre, tipFiltre, durumFiltre, minFiyat, maxFiyat, sortBy]);

    useEffect(() => { setDaireSayfa(1); }, [akilliKriter, blokFiltre, tipFiltre, durumFiltre, minFiyat, maxFiyat, sortBy]);
    const DAIRE_SAYFA_BOYUTU = 15;
    const daireToplamSayfa = Math.max(1, Math.ceil(filtrelenmisDaireler.length / DAIRE_SAYFA_BOYUTU));
    const daireSayfadaki = filtrelenmisDaireler.slice((daireSayfa - 1) * DAIRE_SAYFA_BOYUTU, daireSayfa * DAIRE_SAYFA_BOYUTU);

    const durumToggle = (value: string) => {
        if (value === '') { setDurumFiltre([]); return; }
        setDurumFiltre(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
    };

    const daireFiltreTemizle = () => {
        setAkilliMetin(''); setBlokFiltre(''); setTipFiltre(''); setDurumFiltre([]); setMinFiyat(''); setMaxFiyat(''); setSortBy('');
    };
    const daireFiltreAktifMi = !!(akilliMetin || blokFiltre || tipFiltre || durumFiltre.length > 0 || minFiyat || maxFiyat || sortBy);

    const gorunenGaleriTabs = veri ? GALERI_TABS.filter(t => {
        if (t.key === 'daireTipleri') return veri.daireTipleri.length > 0;
        if (t.key === 'video') return veri.videolar.length > 0;
        const b = (veri as any)[t.key] as Bolum;
        return b && (b.gorseller.length > 0 || b.maddeler.length > 0);
    }) : [];
    const aktifGaleriTab = gorunenGaleriTabs.some(t => t.key === galeriTab) ? galeriTab : (gorunenGaleriTabs[0]?.key ?? '');

    const GALERI_KATEGORI_ADI: Record<string, string> = {
        disMekan: 'Dış Mekan', konumCevre: 'Konum / Çevre', icMekan: 'İç Mekan', sosyalAlan: 'Sosyal Alanlar',
    };
    const tumGaleriResimleri = useMemo<GaleriResim[]>(() => {
        if (!veri) return [];
        const list: GaleriResim[] = [];
        (['disMekan', 'konumCevre', 'icMekan', 'sosyalAlan'] as const).forEach(key => {
            const b = veri[key] as Bolum;
            b.gorseller.forEach(g => list.push({ gorselYolu: g.gorselYolu, aciklama: g.aciklama, kisim: GALERI_KATEGORI_ADI[key] }));
        });
        return list;
    }, [veri]);

    const openGaleriLightbox = (kisim: string, gorselYolu: string) => {
        const idx = tumGaleriResimleri.findIndex(r => r.kisim === kisim && r.gorselYolu === gorselYolu);
        if (idx >= 0) setGaleriLightboxIndex(idx);
    };
    const galeriResimIleri = () => setGaleriLightboxIndex(i => (i === null || tumGaleriResimleri.length === 0) ? i : (i + 1) % tumGaleriResimleri.length);
    const galeriResimGeri = () => setGaleriLightboxIndex(i => (i === null || tumGaleriResimleri.length === 0) ? i : (i - 1 + tumGaleriResimleri.length) % tumGaleriResimleri.length);

    useEffect(() => {
        if (galeriLightboxIndex === null) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight') galeriResimIleri();
            if (e.key === 'ArrowLeft') galeriResimGeri();
            if (e.key === 'Escape') setGaleriLightboxIndex(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [galeriLightboxIndex, tumGaleriResimleri.length]);

    const gorunenNav = veri ? NAV.filter(n => {
        if (n.key === 'hakkinda') return !!veri.hakkindaMetni || !!veri.projeLogo;
        if (n.key === 'ozellikler') return veri.ozellikler.length > 0;
        if (n.key === 'vaziyet') return veri.vaziyetPlaniGorselleri.length > 0;
        if (n.key === 'galeri') return gorunenGaleriTabs.length > 0;
        if (n.key === 'bloklar') return bloklarOzet.length > 0;
        if (n.key === 'daireler') return true;
        if (n.key === 'gorunumler') return true;
        return false;
    }) : [];

    const scrollTo = (key: string) => {
        setAktifNav(key);
        if (key === 'daireler') setDairelerAcik(true);
        document.getElementById(key)?.scrollIntoView({ behavior: 'smooth' });
    };

    if (yukleniyor) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i> Proje sayfası yükleniyor...
            </div>
        );
    }

    if (!veri) {
        return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Proje bulunamadı.</div>;
    }

    return (
        <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", background: '#fff', minHeight: '100vh' }}>
            {/* Hero */}
            <div style={{ position: 'relative', height: 'min(52vh, 460px)', background: '#0f172a', overflow: 'hidden' }}>
                {veri.kapakGorselleri.length > 0 ? (
                    <img src={`${API_BASE_URL}/userFiles/${veri.kapakGorselleri[kapakIndex]?.gorselYolu}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#334155,#0f172a)' }}>
                        <i className="fas fa-building" style={{ fontSize: 56, color: 'rgba(255,255,255,0.25)' }}></i>
                    </div>
                )}
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(15,23,42,0.85), rgba(15,23,42,0.1) 60%)' }} />
                <button onClick={() => navigate(-1)} title="Geri" style={{ position: 'absolute', top: 20, left: 20, width: 38, height: 38, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(6px)', color: '#fff', cursor: 'pointer' }}>
                    <i className="fas fa-arrow-left"></i>
                </button>
                {veri.kapakGorselleri.length > 1 && (
                    <div style={{ position: 'absolute', bottom: 90, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 6 }}>
                        {veri.kapakGorselleri.map((_, i) => (
                            <button key={i} onClick={() => setKapakIndex(i)}
                                style={{ width: i === kapakIndex ? 20 : 7, height: 7, borderRadius: 9999, border: 'none', background: i === kapakIndex ? '#fff' : 'rgba(255,255,255,0.5)', cursor: 'pointer', transition: 'all 0.2s' }} />
                        ))}
                    </div>
                )}
                <div style={{ position: 'absolute', bottom: 24, left: 24, right: 24, display: 'flex', alignItems: 'flex-end', gap: 16 }}>
                    {veri.projeLogo && (
                        <img src={`${API_BASE_URL}/userFiles/${veri.projeLogo}`} alt="" style={{ width: 64, height: 64, borderRadius: 14, objectFit: 'cover', border: '3px solid rgba(255,255,255,0.9)', boxShadow: '0 8px 20px rgba(0,0,0,0.3)' }} />
                    )}
                    <h1 style={{ color: '#fff', fontSize: 32, fontWeight: 800, margin: 0, textShadow: '0 2px 12px rgba(0,0,0,0.4)' }}>{veri.projeAdi}</h1>
                </div>
            </div>

            {/* Yatay, üstte yapışkan nav — manşetin hemen altında */}
            <div style={{ position: 'sticky', top: 0, zIndex: 20, background: '#fff', borderBottom: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(15,23,42,0.05)' }}>
                <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', display: 'flex', gap: 4, overflowX: 'auto' }}>
                    {gorunenNav.map(n => (
                        <button key={n.key} onClick={() => scrollTo(n.key)}
                            style={{
                                padding: '15px 16px', border: 'none', background: 'none', cursor: 'pointer',
                                borderBottom: aktifNav === n.key ? '2px solid #cd6906' : '2px solid transparent',
                                color: aktifNav === n.key ? '#cd6906' : '#64748b', fontWeight: aktifNav === n.key ? 700 : 500,
                                fontSize: 13.5, whiteSpace: 'nowrap', flexShrink: 0,
                            }}>
                            {n.ad}
                        </button>
                    ))}
                </div>
            </div>

            {/* İçerik */}
            <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 24px 80px' }}>
                    {(veri.hakkindaMetni || veri.kapakGorselleri.length > 0) && (
                        <section id="hakkinda" style={{ marginBottom: 48, scrollMarginTop: 70 }}>
                            <h2 style={psH2}>Proje Hakkında</h2>
                            <p style={{ fontSize: 14.5, lineHeight: 1.8, color: '#334155', whiteSpace: 'pre-wrap' }}>{veri.hakkindaMetni || 'Bu proje için henüz açıklama eklenmedi.'}</p>
                        </section>
                    )}

                    {veri.ozellikler.length > 0 && (
                        <section id="ozellikler" style={{ marginBottom: 48, scrollMarginTop: 70 }}>
                            <h2 style={psH2}>Proje Özellikleri</h2>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                                {veri.ozellikler.map(o => (
                                    <div key={o.projeOzellikId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#f8fafc', borderRadius: 10 }}>
                                        <i className={o.ikon || 'fas fa-check-circle'} style={{ color: '#cd6906', fontSize: 15 }}></i>
                                        <span style={{ fontSize: 13.5, color: '#334155' }}><b>{o.ozellik}:</b> {o.deger}</span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {veri.vaziyetPlaniGorselleri.length > 0 && (
                        <section id="vaziyet" style={{ marginBottom: 48, scrollMarginTop: 70 }}>
                            <h2 style={psH2}>Vaziyet Planı</h2>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
                                {veri.vaziyetPlaniGorselleri.map(g => <GorselKart key={g.projeGaleriID} oge={g} onClick={() => setLightbox(g.gorselYolu)} />)}
                            </div>
                        </section>
                    )}

                    {gorunenGaleriTabs.length > 0 && (
                        <section id="galeri" style={{ marginBottom: 48, scrollMarginTop: 70 }}>
                            <h2 style={psH2}>Galeri</h2>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 22, borderBottom: '1px solid #f1f5f9', paddingBottom: 4 }}>
                                {gorunenGaleriTabs.map(t => (
                                    <button key={t.key} onClick={() => setGaleriTab(t.key)}
                                        style={{
                                            padding: '8px 16px', borderRadius: 9999, border: 'none', cursor: 'pointer',
                                            background: aktifGaleriTab === t.key ? '#0f172a' : '#f1f5f9',
                                            color: aktifGaleriTab === t.key ? '#fff' : '#64748b',
                                            fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
                                        }}>
                                        {t.ad}
                                    </button>
                                ))}
                            </div>

                            {aktifGaleriTab === 'daireTipleri' && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
                                    {veri.daireTipleri.map(tip => (
                                        <div key={tip.daireTipID} onClick={() => tip.bolumler.length > 0 && setDetayTip(tip)}
                                            style={{ border: '1px solid #e2e8f0', borderRadius: 14, padding: 20, textAlign: 'center', cursor: tip.bolumler.length > 0 ? 'pointer' : 'default', transition: 'box-shadow 0.15s' }}
                                            onMouseEnter={e => { if (tip.bolumler.length > 0) e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; }}
                                            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}>
                                            <i className={tip.ikon || 'fas fa-door-open'} style={{ fontSize: 26, color: '#cd6906', marginBottom: 10, display: 'block' }}></i>
                                            <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>{tip.tip}</div>
                                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>{daireTipiSayilari.get(tip.daireTipID) ?? 0} daire</div>
                                            {tip.bolumler.length > 0 && <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 4 }}>{tip.bolumler.length} detay →</div>}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {aktifGaleriTab === 'video' && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                                    {veri.videolar.map(v => {
                                        const embed = youtubeEmbed(v.youtubeLink);
                                        return (
                                            <div key={v.projeVideoID}>
                                                {embed && (
                                                    <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 12, overflow: 'hidden', background: '#000' }}>
                                                        <iframe src={embed} title={v.baslik || 'Video'} allowFullScreen
                                                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} />
                                                    </div>
                                                )}
                                                {v.baslik && <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 6 }}>{v.baslik}</div>}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {aktifGaleriTab === 'disMekan' && <BolumIcerik bolum={veri.disMekan} onGorsel={g => openGaleriLightbox('Dış Mekan', g.gorselYolu)} />}
                            {aktifGaleriTab === 'konumCevre' && <BolumIcerik bolum={veri.konumCevre} onGorsel={g => openGaleriLightbox('Konum / Çevre', g.gorselYolu)} />}
                            {aktifGaleriTab === 'icMekan' && <BolumIcerik bolum={veri.icMekan} onGorsel={g => openGaleriLightbox('İç Mekan', g.gorselYolu)} />}
                            {aktifGaleriTab === 'sosyalAlan' && <BolumIcerik bolum={veri.sosyalAlan} onGorsel={g => openGaleriLightbox('Sosyal Alanlar', g.gorselYolu)} />}
                        </section>
                    )}

                    {bloklarOzet.length > 0 && (
                        <section id="bloklar" style={{ marginBottom: 48, scrollMarginTop: 70 }}>
                            <h2 style={psH2}>Bloklar</h2>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
                                {bloklarOzet.map(b => (
                                    <div key={b.blokID} style={{ border: '1px solid #e2e8f0', borderRadius: 14, padding: '18px 20px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                                            <span style={{ width: 36, height: 36, borderRadius: 10, background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <i className="fas fa-building" style={{ color: '#cd6906', fontSize: 15 }}></i>
                                            </span>
                                            <div style={{ fontWeight: 700, fontSize: 15.5, color: '#0f172a' }}>{b.blokAdi}</div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }}>
                                                <span style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6 }}><i className="fas fa-layer-group" style={{ width: 14, textAlign: 'center' }}></i>Kat Sayısı</span>
                                                <span style={{ fontWeight: 700, color: '#334155' }}>{b.katSayisi}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }}>
                                                <span style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6 }}><i className="fas fa-door-open" style={{ width: 14, textAlign: 'center' }}></i>Daire Sayısı</span>
                                                <span style={{ fontWeight: 700, color: '#334155' }}>{b.daireSayisi}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }}>
                                                <span style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6 }}><i className="fas fa-th-large" style={{ width: 14, textAlign: 'center' }}></i>Farklı Daire Tipi</span>
                                                <span style={{ fontWeight: 700, color: '#334155' }}>{b.daireTipiSayisi}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    <section id="gorunumler" style={{ marginBottom: 48, scrollMarginTop: 70 }}>
                        <h2 style={psH2}>Görünümler</h2>
                        {gorunumlerYukleniyor ? (
                            <div style={{ color: '#94a3b8', fontSize: 13, padding: '20px 0' }}><i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Görünümler yükleniyor...</div>
                        ) : gorunumlerListesi.length === 0 ? (
                            <div style={{ color: '#94a3b8', fontSize: 13, padding: '20px 0' }}>Bu proje için henüz görünüm oluşturulmadı.</div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
                                {gorunumlerListesi.map(g => (
                                    <div key={g.gorunumID} onClick={() => acGorunum(g.gorunumID)}
                                        style={{ position: 'relative', aspectRatio: '4/3', borderRadius: 12, overflow: 'hidden', cursor: gorunumLookupYukleniyor ? 'wait' : 'pointer', background: '#f1f5f9', border: '1px solid #e2e8f0' }}>
                                        {g.arkaplanGorsel ? (
                                            <img src={`${API_BASE_URL}/api/ProjeGorunumleri/gorsel/${g.arkaplanGorsel}`} alt={g.gorunumAdi} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                        ) : (
                                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <i className="fas fa-layer-group" style={{ fontSize: 28, color: '#cbd5e1' }}></i>
                                            </div>
                                        )}
                                        <div style={{ position: 'absolute', inset: 'auto 0 0 0', background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', color: '#fff', fontSize: 13, fontWeight: 700, padding: '24px 12px 10px' }}>
                                            {g.gorunumAdi}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    <section id="daireler" style={{ marginBottom: 48, scrollMarginTop: 70, border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden' }}>
                        <button onClick={() => setDairelerAcik(a => !a)}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '18px 22px', border: 'none', background: dairelerAcik ? '#fff' : '#f8fafc', cursor: 'pointer', textAlign: 'left' }}>
                            <span style={{ ...psH2, marginBottom: 0, flex: 1 }}>Daireler</span>
                            {!daireYukleniyor && daireler.length > 0 && (
                                <span style={{ fontSize: 12.5, color: '#64748b', fontWeight: 600 }}>
                                    {statCounts.toplam} daire · <span style={{ color: '#15803d' }}>{statCounts.satista} satışta</span>
                                </span>
                            )}
                            <i className="fas fa-chevron-down" style={{ color: '#94a3b8', transition: 'transform 0.2s', transform: dairelerAcik ? 'rotate(180deg)' : 'none' }}></i>
                        </button>

                        {dairelerAcik && <div style={{ padding: '4px 22px 22px' }}>
                        {daireYukleniyor ? (
                            <div style={{ color: '#94a3b8', fontSize: 13, padding: '20px 0' }}><i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Daireler yükleniyor...</div>
                        ) : daireler.length === 0 ? (
                            <div style={{ color: '#94a3b8', fontSize: 13, padding: '20px 0' }}>Bu projede henüz daire tanımlanmadı.</div>
                        ) : (
                            <>
                                {/* Durum istatistik kartları */}
                                <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                                    {([
                                        { label: 'Toplam Daire', value: '', count: statCounts.toplam, ikon: 'fa-building' },
                                        { label: 'Satışta', value: 'Satışta', count: statCounts.satista, ikon: 'fa-tag' },
                                        { label: 'Rezerve', value: 'Rezerve', count: statCounts.rezerve, ikon: 'fa-clock' },
                                        { label: 'Sözleşme', value: 'Sözleşme', count: statCounts.sozlesme, ikon: 'fa-file-signature' },
                                    ]).map(s => {
                                        const aktif = s.value === '' ? durumFiltre.length === 0 : durumFiltre.includes(s.value);
                                        return (
                                            <div key={s.label} onClick={() => durumToggle(s.value)}
                                                style={{ display: 'flex', alignItems: 'center', gap: 12, background: aktif ? '#cd6906' : '#f8fafc', borderRadius: 10, padding: '10px 16px', cursor: 'pointer', boxShadow: aktif ? '0 4px 14px rgba(205,105,6,0.25)' : 'none', transition: 'all 0.15s' }}>
                                                <span style={{ width: 30, height: 30, borderRadius: 8, background: aktif ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                    <i className={`fas ${s.ikon}`} style={{ color: aktif ? '#fff' : '#94a3b8', fontSize: 13 }}></i>
                                                </span>
                                                <span style={{ fontSize: 20, fontWeight: 800, color: aktif ? '#fff' : '#0f172a', lineHeight: 1 }}>{s.count}</span>
                                                <span style={{ fontSize: 11.5, fontWeight: 600, color: aktif ? 'rgba(255,255,255,0.9)' : '#6b7280' }}>{s.label}</span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Filtre çubuğu */}
                                <div style={{ background: '#fff', border: '1px solid #f1f5f9', borderRadius: 12, padding: '12px 14px', marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {/* Row 0: Akıllı Arama */}
                                    <div style={{ position: 'relative', maxWidth: 420 }}>
                                        <i className="fas fa-wand-magic-sparkles" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#cd6906', fontSize: 13 }}></i>
                                        <input
                                            value={akilliMetin}
                                            onChange={e => setAkilliMetin(e.target.value)}
                                            placeholder="Akıllı Arama: örn. 3+1 güney cephe 2. kat"
                                            style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 9999, padding: '9px 34px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                        />
                                        {akilliMetin && (
                                            <button onClick={() => setAkilliMetin('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer' }}>
                                                <i className="fas fa-times-circle"></i>
                                            </button>
                                        )}
                                    </div>
                                    {akilliCipler.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                            {akilliCipler.map(c => (
                                                <span key={c.anahtar} style={{ background: '#fff7ed', color: '#cd6906', fontSize: 11.5, fontWeight: 600, padding: '4px 10px', borderRadius: 9999 }}>{c.etiket}</span>
                                            ))}
                                        </div>
                                    )}

                                    {/* Row 1: Blok + Tip + Durum pilleri */}
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                        {bloklarListesi.length > 0 && (
                                            <select value={blokFiltre} onChange={e => setBlokFiltre(e.target.value)}
                                                style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 12, fontWeight: 600, color: '#475569', outline: 'none', cursor: 'pointer' }}>
                                                <option value="">Tüm Bloklar</option>
                                                {bloklarListesi.map(b => <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>)}
                                            </select>
                                        )}
                                        {tipListesi.length > 0 && (
                                            <select value={tipFiltre} onChange={e => setTipFiltre(e.target.value)}
                                                style={{ padding: '6px 10px', borderRadius: 8, border: `1.5px solid ${tipFiltre ? '#cd6906' : '#e2e8f0'}`, background: '#f8fafc', fontSize: 12, fontWeight: 600, color: tipFiltre ? '#cd6906' : '#475569', outline: 'none', cursor: 'pointer' }}>
                                                <option value="">Tüm Tipler</option>
                                                {tipListesi.map(tip => <option key={tip} value={tip}>{tip}</option>)}
                                            </select>
                                        )}
                                        <div style={{ width: 1, height: 22, background: '#e2e8f0' }} />
                                        {DURUM_PILLS_PS.map(pill => {
                                            const aktif = pill.value === '' ? durumFiltre.length === 0 : durumFiltre.includes(pill.value);
                                            return (
                                                <button key={pill.value} onClick={() => durumToggle(pill.value)}
                                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 11px', borderRadius: 9999, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', border: aktif ? `1.5px solid ${pill.value === '' ? '#cd6906' : pill.dot}` : '1.5px solid #e2e8f0', background: aktif ? (pill.value === '' ? '#cd6906' : pill.dot) : '#fff', color: aktif ? '#fff' : '#64748b' }}>
                                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: aktif ? 'rgba(255,255,255,0.8)' : pill.dot }} />
                                                    {pill.label}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Row 2: Fiyat aralığı + Sıralama + Temizle */}
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <input type="text" placeholder="Min ₺" value={minFiyat} onChange={e => setMinFiyat(formatFiyatStr(e.target.value))}
                                            style={{ width: 100, padding: '6px 9px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 12, color: '#475569', outline: 'none' }} />
                                        <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>
                                        <input type="text" placeholder="Max ₺" value={maxFiyat} onChange={e => setMaxFiyat(formatFiyatStr(e.target.value))}
                                            style={{ width: 100, padding: '6px 9px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 12, color: '#475569', outline: 'none' }} />
                                        <div style={{ width: 1, height: 20, background: '#e2e8f0' }} />
                                        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                                            style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 12, fontWeight: 600, color: '#475569', outline: 'none', cursor: 'pointer' }}>
                                            <option value="">Sıralama</option>
                                            <option value="fiyat-artan">Fiyat ↑</option>
                                            <option value="fiyat-azalan">Fiyat ↓</option>
                                            <option value="daire-no">Daire No</option>
                                        </select>
                                        {daireFiltreAktifMi && (
                                            <button onClick={daireFiltreTemizle}
                                                style={{ padding: '4px 12px', borderRadius: 9999, border: '1.5px solid #fca5a5', background: '#fff', color: '#dc2626', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                <i className="fas fa-times"></i> Temizle
                                            </button>
                                        )}
                                        <button onClick={() => navigate('/daireler/liste', { state: { projeID: veri.projeID } })}
                                            style={{ marginLeft: 'auto', border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                            Tüm Daireler Listesinde Aç <i className="fas fa-arrow-up-right-from-square" style={{ marginLeft: 6 }}></i>
                                        </button>
                                    </div>
                                </div>

                                {/* Sonuç etiketi + sütun görünürlüğü */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>
                                        {filtrelenmisDaireler.length} kayıt listeleniyor{filtrelenmisDaireler.length !== daireler.length && ` (${daireler.length} toplam)`}
                                    </span>
                                    <span style={{ fontSize: 11.5, color: '#cbd5e1' }}>Sayfa {daireSayfa} / {daireToplamSayfa}</span>
                                </div>
                                <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #f1f5f9', padding: '7px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginRight: 6 }}>Sütunlar:</span>
                                    {([
                                        { key: 'cephe', label: 'Cephe' },
                                        { key: 'manzara', label: 'Manzara' },
                                        { key: 'm2', label: 'm²' },
                                        { key: 'fiyat', label: 'Fiyat' },
                                    ] as { key: keyof typeof colVisible; label: string }[]).map(col => {
                                        const checked = colVisible[col.key];
                                        return (
                                            <label key={col.key} onClick={() => setColVisible(prev => ({ ...prev, [col.key]: !prev[col.key] }))}
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '3px 9px', borderRadius: 9999, border: `1.5px solid ${checked ? '#cd6906' : '#e2e8f0'}`, background: checked ? '#fff8f0' : '#f8fafc', fontSize: 11.5, fontWeight: 600, color: checked ? '#cd6906' : '#94a3b8' }}>
                                                {col.label}
                                            </label>
                                        );
                                    })}
                                </div>

                                {/* Tablo */}
                                {filtrelenmisDaireler.length === 0 ? (
                                    <div style={{ color: '#94a3b8', fontSize: 13, padding: '30px 0', textAlign: 'center' }}>Bu kritere uyan daire bulunamadı.</div>
                                ) : (
                                    <div style={{ overflowX: 'auto', border: '1px solid #f1f5f9', borderRadius: 12 }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                            <thead>
                                                <tr style={{ background: '#1a2340' }}>
                                                    <th style={psTh}>#</th>
                                                    <th style={psTh}>Daire No</th>
                                                    <th style={psTh}>Blok</th>
                                                    <th style={psTh}>Kat</th>
                                                    <th style={psTh}>Tip</th>
                                                    {colVisible.cephe && <th style={psTh}>Cephe</th>}
                                                    {colVisible.manzara && <th style={psTh}>Manzara</th>}
                                                    {colVisible.m2 && <th style={{ ...psTh, textAlign: 'right' }}>Net m²</th>}
                                                    {colVisible.m2 && <th style={{ ...psTh, textAlign: 'right' }}>Brüt m²</th>}
                                                    {colVisible.fiyat && <th style={{ ...psTh, textAlign: 'right' }}>Fiyat</th>}
                                                    <th style={{ ...psTh, textAlign: 'center' }}>Durum</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {daireSayfadaki.map((d, idx) => {
                                                    const durum = durumStil(d.daireDurumu);
                                                    const rowBg = idx % 2 === 0 ? '#fff' : '#f8fafc';
                                                    return (
                                                        <tr key={d.daireID} onClick={() => setDetayDaire(d)} style={{ background: rowBg, cursor: 'pointer' }}
                                                            onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = '#fff8f0'; }}
                                                            onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = rowBg; }}>
                                                            <td style={{ ...psTd, color: '#94a3b8' }}>{(daireSayfa - 1) * DAIRE_SAYFA_BOYUTU + idx + 1}</td>
                                                            <td style={psTd}>
                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                                    <i className="fas fa-door-open" style={{ color: '#cd6906', fontSize: 13 }}></i>
                                                                    <span style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>{d.daireNo}</span>
                                                                </span>
                                                            </td>
                                                            <td style={{ ...psTd, fontWeight: 600 }}>{d.blok?.blokAdi || '—'}</td>
                                                            <td style={{ ...psTd, fontWeight: 600 }}>{d.kat?.katAdi || '—'}</td>
                                                            <td style={psTd}>{d.daireTipi?.tip || '—'}</td>
                                                            {colVisible.cephe && <td style={psTd}>{d.cephe?.cepheAdi || '—'}</td>}
                                                            {colVisible.manzara && <td style={psTd}>{d.manzara?.manzaraAdi || '—'}</td>}
                                                            {colVisible.m2 && <td style={{ ...psTd, textAlign: 'right' }}>{d.netM2 ?? '—'}</td>}
                                                            {colVisible.m2 && <td style={{ ...psTd, textAlign: 'right' }}>{d.brutM2 ?? '—'}</td>}
                                                            {colVisible.fiyat && <td style={{ ...psTd, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtFiyat(d.listeFiyati)}</td>}
                                                            <td style={{ ...psTd, textAlign: 'center' }}>
                                                                <span style={{ background: durum.bg, color: durum.color, fontSize: 11, fontWeight: 700, padding: '4px 11px', borderRadius: 9999, whiteSpace: 'nowrap' }}>{d.daireDurumu}</span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Sayfalama */}
                                {daireToplamSayfa > 1 && (
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 14 }}>
                                        <button onClick={() => setDaireSayfa(p => Math.max(1, p - 1))} disabled={daireSayfa === 1}
                                            style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: daireSayfa === 1 ? '#cbd5e1' : '#475569', fontSize: 12, cursor: daireSayfa === 1 ? 'default' : 'pointer' }}>‹ Önceki</button>
                                        <span style={{ padding: '5px 10px', fontSize: 12, color: '#94a3b8' }}>{daireSayfa} / {daireToplamSayfa}</span>
                                        <button onClick={() => setDaireSayfa(p => Math.min(daireToplamSayfa, p + 1))} disabled={daireSayfa === daireToplamSayfa}
                                            style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: daireSayfa === daireToplamSayfa ? '#cbd5e1' : '#475569', fontSize: 12, cursor: daireSayfa === daireToplamSayfa ? 'default' : 'pointer' }}>Sonraki ›</button>
                                    </div>
                                )}
                            </>
                        )}
                        </div>}
                    </section>
            </div>

            {acikGorunumID !== null && veri && (
                <ProjeGorunumleri
                    projeID={veri.projeID}
                    daireler={daireler as any}
                    bloklar={gorunumBloklar}
                    katlar={gorunumKatlar}
                    cepheler={gorunumCepheler}
                    manzaralar={gorunumManzaralar}
                    daireTipleri={gorunumDaireTipleri}
                    initialGorunumID={acikGorunumID}
                    onClose={() => setAcikGorunumID(null)}
                    readOnly={true}
                />
            )}

            {/* Lightbox — tekil (Vaziyet Planı, Daire Tipi bölüm görselleri) */}
            {lightbox && (
                <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 3200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'zoom-out' }}>
                    <img src={`${API_BASE_URL}/userFiles/${lightbox}`} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }} />
                </div>
            )}

            {/* Galeri Lightbox — Dış Mekan/Konum-Çevre/İç Mekan/Sosyal Alanlar arası ileri/geri gezinme */}
            {galeriLightboxIndex !== null && tumGaleriResimleri[galeriLightboxIndex] && (() => {
                const r = tumGaleriResimleri[galeriLightboxIndex];
                return (
                    <div onClick={() => setGaleriLightboxIndex(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 3100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                        <button onClick={() => setGaleriLightboxIndex(null)} title="Kapat (Esc)"
                            style={{ position: 'absolute', top: 20, right: 20, width: 38, height: 38, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.12)', color: '#fff', cursor: 'pointer', zIndex: 1 }}>
                            <i className="fas fa-times"></i>
                        </button>
                        {tumGaleriResimleri.length > 1 && (
                            <button onClick={e => { e.stopPropagation(); galeriResimGeri(); }} title="Önceki"
                                style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', width: 46, height: 46, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.12)', color: '#fff', cursor: 'pointer' }}>
                                <i className="fas fa-chevron-left"></i>
                            </button>
                        )}
                        <img onClick={e => e.stopPropagation()} src={`${API_BASE_URL}/userFiles/${r.gorselYolu}`} alt={r.aciklama || ''}
                            style={{ maxWidth: '85%', maxHeight: '78%', objectFit: 'contain', borderRadius: 8 }} />
                        {tumGaleriResimleri.length > 1 && (
                            <button onClick={e => { e.stopPropagation(); galeriResimIleri(); }} title="Sonraki"
                                style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', width: 46, height: 46, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.12)', color: '#fff', cursor: 'pointer' }}>
                                <i className="fas fa-chevron-right"></i>
                            </button>
                        )}
                        <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', textAlign: 'center', maxWidth: '80%' }}>
                            {r.aciklama && <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{r.aciklama}</div>}
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.85)', fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 9999 }}>
                                <i className="fas fa-tag" style={{ fontSize: 10 }}></i>{r.kisim}
                                <span style={{ opacity: 0.6 }}>· {galeriLightboxIndex + 1}/{tumGaleriResimleri.length}</span>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Daire Tipi Detay */}
            {detayTip && (
                <DaireTipiModal
                    tip={detayTip}
                    daireSayisi={daireTipiSayilari.get(detayTip.daireTipID) ?? 0}
                    onClose={() => setDetayTip(null)}
                    onZoom={setLightbox}
                />
            )}

            {/* Daire Detay Modalı — Daireler Listesi'ndeki yapıyla aynı (salt-okunur) */}
            {detayDaire && (
                <DaireDetayModal
                    daire={{
                        daireID: detayDaire.daireID,
                        daireNo: detayDaire.daireNo,
                        daireDurumu: detayDaire.daireDurumu,
                        projeID: detayDaire.proje?.projeID ?? veri.projeID,
                        blokID: detayDaire.blokID,
                        takipNo: detayDaire.takipNo,
                        aciklama: detayDaire.aciklama,
                        listeFiyati: detayDaire.listeFiyati,
                        netM2: detayDaire.netM2,
                        brutM2: detayDaire.brutM2,
                        proje: detayDaire.proje,
                        blok: detayDaire.blok,
                        kat: detayDaire.kat,
                        cephe: detayDaire.cephe,
                        manzara: detayDaire.manzara,
                        daireTipi: detayDaire.daireTipi,
                    }}
                    onClose={() => setDetayDaire(null)}
                />
            )}
        </div>
    );
};

const psH2: React.CSSProperties = { fontSize: 19, fontWeight: 700, color: '#0f172a', marginTop: 0, marginBottom: 16 };
const psTh: React.CSSProperties = { padding: '11px 14px', textAlign: 'left', color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: '0.4px', whiteSpace: 'nowrap' };
const psTd: React.CSSProperties = { padding: '10px 14px', borderBottom: '1px solid #f1f5f9', color: '#334155' };

export default ProjeSayfaGoruntule;
